import { useState, useEffect, useRef, useCallback } from "react";
import UMapsWordmark from './components/UMapsWordmark.jsx';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_KEY;
const CAMPUS_CENTER = { lat: 49.8097, lng: -97.1335 };
const CAMPUS_ZOOM   = 17;

// ── Pure helpers ──────────────────────────────────────────────────────────────

function slugify(name) {
  return String(name)
    .toLowerCase()
    .replace(/\s*[–—]\s*/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function isPaidNow(paidSchedule, overrideMinutes) {
  if (!paidSchedule) return false;
  const now = overrideMinutes != null ? null : new Date();
  const day = now ? now.getDay() : 2; // weekday 2 = Tuesday, safe default
  const min = overrideMinutes != null ? overrideMinutes : (now.getHours() * 60 + now.getMinutes());
  if (paidSchedule.type === "weekday_window") {
    if (overrideMinutes == null && (day === 0 || day === 6)) return false;
    return min >= 7 * 60 + 30 && min < 16 * 60 + 30;
  }
  if (paidSchedule.type === "after_hours") {
    if (overrideMinutes == null && (day === 0 || day === 6)) return true;
    return min >= 16 * 60 + 30 || min < 7 * 60 + 30;
  }
  if (paidSchedule.type === "parkade") return true;
  return false;
}

function isNLot(name) {
  return /^N\s*[–-]/i.test(name) || /^N\s+(Lot|After|Accessible|Paid)/i.test(name);
}

function formatSchedule(paidSchedule, entryName) {
  if (!paidSchedule) return "—";
  if (paidSchedule.type === "weekday_window") return "Mon–Fri, 7:30 AM – 4:30 PM";
  if (paidSchedule.type === "after_hours") {
    if (isNLot(entryName || "")) {
      return "Free weekdays 7:30 AM – 4:30 PM (permit holders)\nPaid $5 flat: M–F 4:30 PM – 7:30 AM / Sat–Sun all day";
    }
    return "Mon–Fri 4:30 PM – 7:30 AM / Sat–Sun all day";
  }
  if (paidSchedule.type === "parkade") return "Daily 6:00 AM – 6:00 PM (day) / 6:00 PM – 6:00 AM (evening)";
  if (paidSchedule.type === "permit_only") return "Permit required (no visitor pay)";
  return "—";
}

const CAT_COLORS = {
  accessible: { fill: "#6a1b9a", stroke: "#4a148c", labelBg: "rgba(200,160,230,0.95)" },
  residence:  { fill: "#a1887f", stroke: "#795548", labelBg: "rgba(200,170,155,0.95)" },
  visitor:    { fill: "#1565c0", stroke: "#0d47a1", labelBg: "rgba(140,190,240,0.95)" },
  student:    { fill: "#f57c00", stroke: "#e65100", labelBg: "rgba(255,185,100,0.95)" },
  staff:      { fill: "#c62828", stroke: "#b71c1c", labelBg: "rgba(240,160,160,0.95)" },
  dropoff:    { fill: "#00897b", stroke: "#00695c", labelBg: "rgba(100,210,200,0.95)" },
  default:    { fill: "#d94f4f", stroke: "#d94f4f", labelBg: "rgba(255,195,80,0.95)" }, // polygon color unchanged
};

function catColors(category) {
  const cat = (category || "").toLowerCase();
  if (cat.includes("accessible")) return CAT_COLORS.accessible;
  if (cat.includes("drop"))       return CAT_COLORS.dropoff;
  if (cat.includes("residence"))  return CAT_COLORS.residence;
  if (cat.includes("visitor") || cat.includes("parkade") || cat.includes("pay-by-plate")) return CAT_COLORS.visitor;
  if (cat.includes("student"))    return CAT_COLORS.student;
  if (cat.includes("staff"))      return CAT_COLORS.staff;
  return CAT_COLORS.default;
}

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) { resolve(); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) { existing.addEventListener("load", resolve); return; }
    const s = document.createElement("script");
    s.id = "gmap-script";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    s.async = true; s.onload = resolve;
    document.head.appendChild(s);
  });
}

const LEGEND_ITEMS = [
  { color: CAT_COLORS.visitor.fill,    border: CAT_COLORS.visitor.stroke,    label: "Paid Visitor",        short: "Paid" },
  { color: CAT_COLORS.student.fill,    border: CAT_COLORS.student.stroke,    label: "Student Permit",      short: "Student" },
  { color: CAT_COLORS.staff.fill,      border: CAT_COLORS.staff.stroke,      label: "Staff Permit",        short: "Staff" },
  { color: CAT_COLORS.accessible.fill, border: CAT_COLORS.accessible.stroke, label: "Accessible",          short: "Accessible" },
  { color: CAT_COLORS.dropoff.fill,    border: CAT_COLORS.dropoff.stroke,    label: "Pickup/Dropoff Zone", short: "Pickup/Dropoff" },
];

// Extract the short lot name (everything before " - ")
function shortName(name) {
  const idx = name.indexOf(" - ");
  return idx !== -1 ? name.slice(0, idx).trim() : name;
}

// ── Component ─────────────────────────────────────────────────────────────────

// parkingFilter values:
//   null          → show all standard lots (accessible hidden)
//   "now"         → lots currently usable by a visitor (real device time; hides permit-only & N lots during day)
//   "paid_hours"  → visitor/paid lots that cost money 7:30–4:30 (simulated 8am weekday)
//   "free_hours"  → after 4:30 PM view: free lots + N lots + Parkcade (simulated 5pm)
//   "accessible"  → only accessible spots
//   "dropoff"     → only drop-off zones

export default function ParkingPage() {
  const mapDivRef       = useRef(null);
  const mapRef          = useRef(null);
  const overlayClassRef = useRef(null);
  const parkingLotsRef  = useRef([]);
  const pendingPanRef   = useRef(null);

  const parkingPolysRef  = useRef([]);
  const parkingLabelsRef = useRef([]);
  const parkingItemsRef  = useRef([]);
  const buildingsRef     = useRef([]);
  const buildingMarkersRef = useRef([]);

  const watchIdRef        = useRef(null);
  const locationMarkerRef = useRef(null);
  const locationCircleRef = useRef(null);

  const [mapReady,        setMapReady]        = useState(false);
  const [parkingLots,     setParkingLots]     = useState([]);
  const [parkingInfoData, setParkingInfoData] = useState(null);
  const [parkingFilter,   setParkingFilter]   = useState(null);
  const [showLocation,    setShowLocation]    = useState(false);
  const [locError,        setLocError]        = useState(null);
  const [linkCopied,      setLinkCopied]      = useState(false);
  const [volunteerOpen,   setVolunteerOpen]   = useState(false);
  const [slideIndex,      setSlideIndex]      = useState(0);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [searchQuery,     setSearchQuery]     = useState("");
  const [searchResults,   setSearchResults]   = useState([]);
  const [searchOpen,      setSearchOpen]      = useState(false);
  const [welcomeOpen,     setWelcomeOpen]     = useState(false);
  const [feedbackOpen,    setFeedbackOpen]    = useState(false);
  const [isMobile,        setIsMobile]        = useState(false);
  const [mapZoom,         setMapZoom]         = useState(CAMPUS_ZOOM);
  const [mapCenter,       setMapCenter]       = useState(CAMPUS_CENTER);
  const [buildings,       setBuildings]       = useState([]);

  // Detect mobile once mounted
  useEffect(() => {
    setIsMobile(window.innerWidth < 640);
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Show welcome popup on every page load, unless arriving via a share link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("p") && !params.get("f")) setWelcomeOpen(true);
  }, []);

  // ── Init map ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY).then(() => {
      if (!mapDivRef.current) return;
      const map = new window.google.maps.Map(mapDivRef.current, {
        center: CAMPUS_CENTER, zoom: CAMPUS_ZOOM,
        mapTypeId: "satellite", tilt: 0,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      mapRef.current = map;

      class ParkingLabel extends window.google.maps.OverlayView {
        constructor(position, html, bgColor, onClick) {
          super();
          this.position = position; this.html = html; this.bgColor = bgColor; this._div = null; this._onClick = onClick;
        }
        onAdd() {
          const div = document.createElement("div");
          div.style.cssText = [
            "position:absolute", `background:${this.bgColor}`,
            "border-radius:4px", "padding:5px 10px",
            "font-family:Arial,sans-serif", "font-size:11px", "font-weight:400",
            "color:#111", "transform:translate(-50%,-50%)",
            "box-shadow:0 1px 5px rgba(0,0,0,0.18)", "letter-spacing:0.01em",
            this._onClick ? "pointer-events:auto;cursor:pointer" : "pointer-events:none",
          ].join(";");
          div.innerHTML = this.html;
          if (this._onClick) div.addEventListener("click", this._onClick);
          this._div = div;
          this.getPanes().floatPane.appendChild(div);
        }
        draw() {
          const pos = this.getProjection().fromLatLngToDivPixel(this.position);
          if (pos && this._div) { this._div.style.left = pos.x + "px"; this._div.style.top = pos.y + "px"; }
        }
        onRemove() {
          if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; }
        }
      }
      overlayClassRef.current = ParkingLabel;
      setMapReady(true);

      map.addListener("idle", () => {
        if (pendingPanRef.current) {
          map.panTo(pendingPanRef.current);
          map.setZoom(19);
          pendingPanRef.current = null;
        }
      });
      map.addListener("zoom_changed", () => setMapZoom(map.getZoom()));
      map.addListener("center_changed", () => {
        const c = map.getCenter();
        if (c) setMapCenter({ lat: c.lat(), lng: c.lng() });
      });
    });

    fetch("/Map%20Info/buildinglabels.json")
      .then((r) => r.json())
      .then((data) => { buildingsRef.current = Array.isArray(data) ? data : []; setBuildings(buildingsRef.current); })
      .catch(() => {});

    fetch(`${process.env.NODE_ENV === "development" ? "http://localhost:3001" : ""}/api/parking`)
      .then((r) => r.json())
      .then((data) => {
        const lots = Array.isArray(data) ? data : [];
        parkingLotsRef.current = lots;
        setParkingLots(lots);
        const urlParams   = new URLSearchParams(window.location.search);
        const param       = urlParams.get("p");
        const filterParam = urlParams.get("f");
        if (filterParam) setParkingFilter(filterParam);
        if (param) {
          for (const entry of lots) {
            if (slugify(entry.name) === param && entry.polygon?.length) {
              setParkingInfoData(entry);
              const n = entry.polygon.length;
              const panTo = {
                lat: entry.polygon.reduce((s, c) => s + c.latitude,  0) / n,
                lng: entry.polygon.reduce((s, c) => s + c.longitude, 0) / n,
              };
              if (mapRef.current) {
                mapRef.current.panTo(panTo);
                mapRef.current.setZoom(19);
              } else {
                pendingPanRef.current = panTo;
              }
              break;
            }
          }
        }
      })
      .catch(() => {});

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // ── Render polygons ───────────────────────────────────────────────────────────
  const renderParking = useCallback(() => {
    const map    = mapRef.current;
    const OClass = overlayClassRef.current;
    if (!map || !OClass) return;

    parkingPolysRef.current.forEach((p) => p.setMap(null));
    parkingLabelsRef.current.forEach((l) => l.setMap(null));
    parkingPolysRef.current  = [];
    parkingLabelsRef.current = [];
    parkingItemsRef.current  = [];

    const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Zoom < 18: collapse to name-only, smaller label. Zoom >= 18: full detail.
    const COLLAPSE_ZOOM = 18;
    const collapsed = (mapRef.current?.getZoom() ?? CAMPUS_ZOOM) < COLLAPSE_ZOOM;

    parkingLotsRef.current.forEach((entry) => {
      if (!entry.polygon || entry.polygon.length < 3) return;
      // Residence lots are not useful to display
      if ((entry.category || "").toLowerCase().includes("residence")) return;
      const colors = catColors(entry.category);
      const paths  = entry.polygon.map((c) => ({ lat: c.latitude, lng: c.longitude }));

      const poly = new window.google.maps.Polygon({
        paths,
        strokeColor: colors.stroke, strokeOpacity: 0.8, strokeWeight: 1.5,
        fillColor: colors.fill, fillOpacity: 0.22, zIndex: 50, clickable: true, map,
      });
      poly.addListener("click",     () => setParkingInfoData(entry));
      poly.addListener("mouseover", () => poly.setOptions({ fillOpacity: 0.45, strokeOpacity: 1 }));
      poly.addListener("mouseout",  () => poly.setOptions({ fillOpacity: 0.22, strokeOpacity: 0.8 }));
      parkingPolysRef.current.push(poly);

      const n   = entry.polygon.length;
      const lat = entry.polygon.reduce((s, c) => s + c.latitude,  0) / n;
      const lng = entry.polygon.reduce((s, c) => s + c.longitude, 0) / n;

      const cat      = (entry.category || "").toLowerCase();
      const isPermit = entry.paidSchedule?.type === "permit_only";
      const isDrop   = cat.includes("drop");
      const hasRate  = entry.hourlyRate && entry.hourlyRate !== "—" && entry.hourlyRate !== "";
      const freeNow  = !isPaidNow(entry.paidSchedule);
      const hasMax   = entry.dailyMax  && entry.dailyMax  !== "—" && entry.dailyMax  !== "";
      const hasLimit = entry.timeLimit && entry.timeLimit !== "—" && entry.timeLimit !== "";
      const extraInfo = hasMax
        ? `<span style="font-size:9px;color:#64748b;font-weight:500;margin-left:5px">Max: ${esc(entry.dailyMax)}</span>`
        : hasLimit
          ? `<span style="font-size:9px;color:#64748b;font-weight:500;margin-left:5px">Limit: ${esc(entry.timeLimit)}</span>`
          : "";

      const showPaidFilter = parkingFilter === "paid_hours";
      const showFreeFilter = parkingFilter === "free_hours";
      const isAfterHours  = entry.paidSchedule?.type === "after_hours";
      const isParkade     = entry.paidSchedule?.type === "parkade";
      const isPermitBased = cat.includes("student") || cat.includes("staff") || cat.includes("carpool");
      let subHtml = "";
      if (isDrop) {
        subHtml = `<div style="font-size:10px;color:#00695c;font-weight:700;margin-top:2px">Pickup/Dropoff</div>`;
      } else if (cat.includes("accessible")) {
        subHtml = `<div style="font-size:10px;color:#4a148c;font-weight:700;margin-top:2px">Accessibility Permit Required</div>`;
      } else if (!isPermit) {
        if (showFreeFilter) {
          // Simulate after-4:30pm view — each lot type gets a distinct label
          if (isAfterHours) {
            // N lot: permit required during biz hours, paid after 4:30 / weekends
            subHtml = `<div style="margin-top:3px;text-align:center"><div style="font-size:10px;font-weight:700;background:#fff;padding:1px 6px;border-radius:4px;color:#555;display:inline-block">Permit Required 7:30am–4:30pm</div><div style="background:#c62828;color:#fff;border-radius:4px;padding:1px 6px;font-size:9px;font-weight:700;margin-top:2px;display:inline-block">PAID AFTER 4:30PM &amp; WEEKENDS</div></div>`;
          } else if (isPermitBased) {
            // Student / Staff / Carpool: open to all after hours — badge goes inline with name
            subHtml = `<div style="margin-top:3px;text-align:center"><div style="background:#2e7d32;color:#fff;border-radius:4px;padding:1px 6px;font-size:9px;font-weight:700;display:inline-block">FREE AFTER 4:30PM &amp; WEEKENDS</div></div>`;
          } else if (!isParkade) {
            // Visitor pay-by-plate: costs money during hours, free after
            subHtml = `<div style="margin-top:3px;text-align:center"><div style="background:#2e7d32;color:#fff;border-radius:4px;padding:1px 7px;font-size:10px;font-weight:700;display:inline-block">FREE (past 4:30PM &amp; Weekends)</div></div>`;
          }
        } else {
          // Default / other filters: show real-time free/paid state
          if (freeNow) {
            if (!showPaidFilter && !isAfterHours) {
              subHtml += `<div style="background:#2e7d32;color:#fff;border-radius:4px;padding:1px 7px;font-size:10px;font-weight:700;margin-top:3px;display:inline-block">FREE NOW</div>`;
            }
            if (hasRate) {
              subHtml += `<div style="background:#fff;border-radius:4px;padding:2px 7px;font-size:10px;font-weight:700;color:#111;margin-top:3px;display:inline-flex;align-items:center;box-shadow:0 1px 3px rgba(0,0,0,0.15)">${esc(entry.hourlyRate)}${extraInfo}</div>`;
            }
          } else {
            if (hasRate) {
              subHtml += `<div style="background:#fff;border-radius:4px;padding:2px 7px;font-size:10px;font-weight:700;color:#111;margin-top:3px;display:inline-flex;align-items:center;box-shadow:0 1px 3px rgba(0,0,0,0.15)">${esc(entry.hourlyRate)}${extraInfo}</div>`;
            }
          }
        }
      }

      const accessiblePrefix = cat.includes("accessible") ? "♿ " : "";
      const nameTag = (showFreeFilter && isPermitBased)
        ? ` <span style="font-size:9px;font-weight:700;background:#e8f5e9;color:#2e7d32;border-radius:4px;padding:1px 5px;vertical-align:middle">Open to All</span>`
        : "";
      const labelHtml = collapsed
        ? `<div style="text-align:center;line-height:1.3"><div style="font-weight:700;font-size:10px">${accessiblePrefix}${esc(shortName(entry.name))}${nameTag}</div></div>`
        : `<div style="text-align:center;line-height:1.4"><div style="font-weight:700;font-size:13px">${accessiblePrefix}${esc(entry.name)}${nameTag}</div>${subHtml}</div>`;
      const label = new OClass(new window.google.maps.LatLng(lat, lng), labelHtml, colors.labelBg, () => setParkingInfoData(entry));
      label.setMap(map);
      parkingLabelsRef.current.push(label);
      parkingItemsRef.current.push({ poly, label, entry });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingFilter, mapZoom]); // mapZoom triggers re-render of labels (collapse/expand)

  useEffect(() => {
    if (mapReady && parkingLots.length > 0) renderParking();
  }, [parkingLots, mapReady, renderParking]);

  // ── Filter logic ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Simulated times for non-realtime filters
    const paidSim = 8 * 60;   // 8:00 AM → business hours → weekday_window = paid
    // freeSim not needed (free_hours now shows all non-permit lots regardless of paid status)

    parkingItemsRef.current.forEach(({ poly, label, entry }) => {
      const cat          = (entry.category || "").toLowerCase();
      const isAccessible = cat.includes("accessible");
      const isDrop       = cat.includes("drop");
      const isPermitOnly = entry.paidSchedule?.type === "permit_only";
      const isAfterHours = entry.paidSchedule?.type === "after_hours"; // N lots
      let visible = true;

      if (parkingFilter === "accessible") {
        visible = isAccessible;
      } else if (parkingFilter === "dropoff") {
        visible = isDrop;
      } else if (parkingFilter === "now") {
        // Show lots a visitor can use right now (real device time)
        // N lots (after_hours): only show during their paid/public hours (evenings & weekends)
        // Permit-based (student/staff/carpool): only show when outside restricted hours (free)
        const isPermitBased = cat.includes("student") || cat.includes("staff") || cat.includes("carpool");
        if (isAccessible || isDrop || isPermitOnly) { visible = false; }
        else if (isAfterHours) { visible = isPaidNow(entry.paidSchedule); }
        else if (isPermitBased) { visible = !isPaidNow(entry.paidSchedule); }
        else { visible = true; }
      } else if (parkingFilter === "paid_hours") {
        const isPermitBased = isPermitOnly || cat.includes("student") || cat.includes("staff") || cat.includes("residence") || cat.includes("carpool");
        if (isAccessible || isDrop || isPermitBased) { visible = false; }
        else visible = isPaidNow(entry.paidSchedule, paidSim);
      } else if (parkingFilter === "free_hours") {
        // After 4:30 PM view: show all lots a visitor can use after hours
        // (free visitor lots + N lots [paid evenings] + Parkcade [always paid])
        if (isAccessible || isDrop || isPermitOnly) { visible = false; }
        else { visible = true; } // includes N lots and Parkcade
      } else {
        // null = all — show everything except permit-only lots
        if (isPermitOnly) visible = false;
      }

      poly.setMap(visible ? map : null);
      label.setMap(visible ? map : null);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingFilter, parkingLots, mapReady, mapZoom]);

  // ── Search buildings + parking lots ──────────────────────────────────────────
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();

    const buildingMatches = buildingsRef.current
      .filter((b) => b.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map((b) => ({ name: b.name, lat: b.lat, lng: b.lng, kind: "building" }));

    const lotMatches = parkingLotsRef.current
      .filter((l) => l.polygon?.length >= 3 && l.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map((l) => {
        const n = l.polygon.length;
        return {
          name: l.name,
          lat: l.polygon.reduce((s, c) => s + c.latitude,  0) / n,
          lng: l.polygon.reduce((s, c) => s + c.longitude, 0) / n,
          kind: "lot",
        };
      });

    setSearchResults([...buildingMatches, ...lotMatches].slice(0, 8));
    setSearchOpen(true);
  }, [searchQuery]);

  const handleSearchSelect = (result) => {
    setSearchQuery(result.name);
    setSearchOpen(false);
    mapRef.current?.panTo({ lat: result.lat, lng: result.lng });
    mapRef.current?.setZoom(19);
    if (result.kind === "lot") {
      const entry = parkingLotsRef.current.find((l) => l.name === result.name);
      if (entry) setParkingInfoData(entry);
    }
  };

  // ── Building labels (zoom >= 18, ExamsPage white-box style) ──────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const BUILDING_LABEL_ZOOM = 18;
    const show = mapZoom >= BUILDING_LABEL_ZOOM;

    // Remove existing overlays
    buildingMarkersRef.current.forEach((o) => o.setMap(null));
    buildingMarkersRef.current = [];

    if (show) {
      buildingsRef.current.forEach((b) => {
        const pos = new window.google.maps.LatLng(b.lat, b.lng);
        class BuildingLabelOverlay extends window.google.maps.OverlayView {
          constructor() { super(); this.div = null; }
          onAdd() {
            const d = document.createElement("div");
            d.style.cssText = [
              "position:absolute",
              "background:rgba(255,255,255,0.92)",
              "border-radius:4px",
              "padding:2px 6px",
              "font-family:Arial,sans-serif",
              "font-size:11px",
              "font-weight:700",
              "color:#1e293b",
              "white-space:nowrap",
              "pointer-events:none",
              "transform:translate(-50%,-50%)",
              "box-shadow:0 1px 4px rgba(0,0,0,0.18)",
              "border:1px solid #cbd5e1",
              "z-index:45",
            ].join(";");
            d.textContent = b.name;
            this.div = d;
            this.getPanes().floatPane.appendChild(d);
          }
          draw() {
            const p = this.getProjection().fromLatLngToDivPixel(pos);
            if (p && this.div) { this.div.style.left = p.x + "px"; this.div.style.top = p.y + "px"; }
          }
          onRemove() {
            if (this.div) { this.div.parentNode?.removeChild(this.div); this.div = null; }
          }
        }
        const overlay = new BuildingLabelOverlay();
        overlay.setMap(map);
        buildingMarkersRef.current.push(overlay);
      });
    }
  }, [mapZoom, buildings]);

  // ── Geolocation ───────────────────────────────────────────────────────────────
  const toggleLocation = useCallback(() => {
    if (showLocation) {
      if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
      if (locationMarkerRef.current) { locationMarkerRef.current.setMap(null); locationMarkerRef.current = null; }
      if (locationCircleRef.current) { locationCircleRef.current.setMap(null); locationCircleRef.current = null; }
      setShowLocation(false); setLocError(null); return;
    }
    if (!navigator.geolocation) { setLocError("Geolocation not supported."); return; }
    setLocError(null);
    const update = (pos) => {
      const { latitude, longitude, accuracy } = pos.coords;
      const map = mapRef.current; if (!map) return;
      const latlng = { lat: latitude, lng: longitude };
      if (!locationMarkerRef.current) {
        locationMarkerRef.current = new window.google.maps.Marker({ position: latlng, map, zIndex: 500, title: "Your location", icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: "#4285f4", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2.5 } });
        locationCircleRef.current = new window.google.maps.Circle({ center: latlng, radius: accuracy, map, fillColor: "#4285f4", fillOpacity: 0.1, strokeColor: "#4285f4", strokeOpacity: 0.35, strokeWeight: 1, zIndex: 499, clickable: false });
        map.panTo(latlng);
      } else {
        locationMarkerRef.current.setPosition(latlng);
        locationCircleRef.current.setCenter(latlng);
        locationCircleRef.current.setRadius(accuracy);
      }
      setShowLocation(true);
    };
    const onError = (err) => { setLocError(err.code === 1 ? "Location permission denied." : "Could not get your location."); setShowLocation(false); };
    watchIdRef.current = navigator.geolocation.watchPosition(update, onError, { enableHighAccuracy: true, maximumAge: 5000 });
  }, [showLocation]);

  // ── Welcome popup select filter ───────────────────────────────────────────────
  const handleWelcomeFilter = (filter) => {
    setParkingFilter(filter);
    setWelcomeOpen(false);
    // Auto-prompt for location once popup closes
    if (!showLocation) toggleLocation();
  };

  // ── Shared button style helpers ───────────────────────────────────────────────
  const filterActive = (key) => parkingFilter === key;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative", width: "100vw", height: "100dvh", overflow: "hidden", fontFamily: "system-ui, sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 20, height: 52, background: "#fff", borderBottom: "1px solid #e2e8f0", boxShadow: "0 1px 8px rgba(0,0,0,0.08)", display: "flex", alignItems: "center", padding: "0 12px", gap: 8 }}>
        {/* Site label — leftmost, grows to fill */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <UMapsWordmark tool="Parking" size={20} style={{ fontSize: 13, color: "#0f172a" }} />
          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1, lineHeight: 1.3 }}>Not officially affiliated with the University of Manitoba.</div>
        </div>
        {/* Instagram — outlined box */}
        <a href="https://www.instagram.com/myumaps/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, textDecoration: "none", border: "1px solid #e2e8f0", borderRadius: 10, padding: "5px 9px" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <radialGradient id="ig-grad-p" cx="30%" cy="107%" r="150%">
                <stop offset="0%" stopColor="#fdf497"/>
                <stop offset="5%" stopColor="#fdf497"/>
                <stop offset="45%" stopColor="#fd5949"/>
                <stop offset="60%" stopColor="#d6249f"/>
                <stop offset="90%" stopColor="#285AEB"/>
              </radialGradient>
            </defs>
            <rect x="2" y="2" width="20" height="20" rx="6" ry="6" fill="url(#ig-grad-p)"/>
            <circle cx="12" cy="12" r="4.5" stroke="#fff" strokeWidth="1.8" fill="none"/>
            <circle cx="17.5" cy="6.5" r="1.2" fill="#fff"/>
          </svg>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>@myumaps</span>
        </a>
        {/* Exams button */}
        <button onClick={() => { window.location.href = "/exams"; }} style={{ background: "rgba(255,255,255,0.95)", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 10px", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", flexShrink: 0 }} title="Exam Maps">
          📋
        </button>
        {/* Volunteer — rightmost */}
        <button onClick={() => { setVolunteerOpen(true); setSlideIndex(0); }} style={{ background: "rgba(255,255,255,0.95)", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 10px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#475569", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <img src="/UMaps Logo Circle Border.png" alt="" style={{ width: 18, height: 18, borderRadius: "50%" }} onError={e => e.target.style.display = "none"} />
          {!isMobile && "Volunteer"}
        </button>
      </div>

      {/* ── Map ── */}
      <div ref={mapDivRef} style={{ position: "absolute", top: 52, left: 0, right: 0, bottom: 0 }} />

      {/* ── Search + Filter row ── */}
      <div style={{ position: "absolute", top: 62, left: isMobile ? 10 : "18%", right: isMobile ? 10 : "18%", zIndex: 15, display: "flex", gap: 8, alignItems: "flex-start", marginTop: 10 }}>

        {/* Search — takes ~55% */}
        <div style={{ flex: "0 0 55%", position: "relative" }}>
          <input
            type="text"
            placeholder="Search buildings & lots…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            style={{ width: "100%", boxSizing: "border-box", padding: "10px 30px 10px 12px", borderRadius: searchOpen && searchResults.length > 0 ? "10px 10px 0 0" : 10, border: "1px solid #e2e8f0", background: "rgba(255,255,255,0.97)", boxShadow: "0 2px 10px rgba(0,0,0,0.10)", fontSize: 13, outline: "none" }}
          />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(""); setSearchResults([]); setSearchOpen(false); }} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", fontSize: 15, color: "#94a3b8", cursor: "pointer", padding: 0 }}>✕</button>
          )}
          {searchOpen && searchResults.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 10px 10px", boxShadow: "0 8px 20px rgba(0,0,0,0.12)", overflow: "hidden", zIndex: 20 }}>
              {searchResults.map((result) => (
                <button key={result.kind + result.name} onMouseDown={() => handleSearchSelect(result)}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", border: "none", borderBottom: "1px solid #f5f5f5", background: "none", cursor: "pointer", textAlign: "left" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{result.name}</div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: result.kind === "lot" ? "#d94f4f" : "#64748b", background: result.kind === "lot" ? "#fff5f5" : "#f1f5f9", borderRadius: 4, padding: "1px 5px", flexShrink: 0 }}>{result.kind === "lot" ? "Parking" : "Building"}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Filters button + panel — takes remaining space */}
        <div style={{ flex: 1, position: "relative" }}>
          {(() => {
            const activeCount = parkingFilter ? 1 : 0;
            return (
              <button onClick={() => setFilterPanelOpen((v) => !v)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: filterPanelOpen ? "#d94f4f" : "rgba(255,255,255,0.97)", border: `1.5px solid ${filterPanelOpen ? "#d94f4f" : "#e2e8f0"}`, borderRadius: filterPanelOpen ? "10px 10px 0 0" : 10, padding: "10px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: filterPanelOpen ? "#fff" : "#475569", boxShadow: filterPanelOpen ? "none" : "0 2px 10px rgba(0,0,0,0.10)", whiteSpace: "nowrap" }}>
                <span>⚙</span> Filters
                {activeCount > 0 && !filterPanelOpen && (
                  <span style={{ background: "#d94f4f", color: "#fff", borderRadius: 10, fontSize: 10, fontWeight: 800, padding: "1px 6px" }}>{activeCount}</span>
                )}
              </button>
            );
          })()}

          {filterPanelOpen && (
            <div style={{ position: "absolute", top: "100%", right: 0, background: "rgba(255,255,255,0.98)", border: "1.5px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(0,0,0,0.14)", padding: "12px 12px 14px", minWidth: 220, zIndex: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: "#94a3b8", marginBottom: 8 }}>SHOW ONLY</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {[
                  { key: null,          label: "All Lots",                icon: "🅿" },
                  { key: "now",         label: "Parking Right Now",       icon: "🕐" },
                  { key: "paid_hours",  label: "Paid (7:30 AM–4:30 PM)", icon: "💳" },
                  { key: "free_hours",  label: "After 4:30 PM",           icon: "🌆" },
                  { key: "accessible",  label: "Accessible Parking",      icon: "♿" },
                ].map(({ key, label, icon }) => {
                  const active = parkingFilter === key;
                  return (
                    <button key={String(key)} onClick={() => { setParkingFilter(key); setFilterPanelOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${active ? "#d94f4f" : "#e2e8f0"}`, background: active ? "#fff5f5" : "#fafafa", color: active ? "#d94f4f" : "#475569", cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500, textAlign: "left" }}>
                      <span style={{ fontSize: 13 }}>{icon}</span> {label}
                      {active && <span style={{ marginLeft: "auto", fontSize: 11, color: "#d94f4f" }}>✓</span>}
                    </button>
                  );
                })}

              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Active filter indicator ── */}
      {parkingFilter && (() => {
        const FILTER_LABELS = {
          now:         "Parking Right Now",
          paid_hours:  "School Hours (Paid)",
          free_hours:  "After 4:30 PM",
          accessible:  "Accessible Parking",
          dropoff:     "Pickup / Dropoff Zones",
        };
        return (
          <div style={{ position: "absolute", top: 122, left: "50%", transform: "translateX(-50%)", zIndex: 14, pointerEvents: "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(217,79,79,0.92)", borderRadius: 20, padding: "5px 14px", boxShadow: "0 2px 8px rgba(0,0,0,0.18)", whiteSpace: "nowrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: 0.2 }}>Filter: {FILTER_LABELS[parkingFilter]}</span>
              <button
                onClick={() => setParkingFilter(null)}
                style={{ pointerEvents: "auto", background: "rgba(255,255,255,0.25)", border: "none", borderRadius: "50%", width: 16, height: 16, fontSize: 10, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 }}
              >✕</button>
            </div>
          </div>
        );
      })()}

      {/* ── Back to UM button ── */}
      {Math.hypot(mapCenter.lat - CAMPUS_CENTER.lat, mapCenter.lng - CAMPUS_CENTER.lng) > 0.018 && (
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 15, pointerEvents: "none" }}>
          <button
            onClick={() => { mapRef.current?.panTo(CAMPUS_CENTER); mapRef.current?.setZoom(CAMPUS_ZOOM); }}
            style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.97)", border: "1.5px solid #d94f4f", borderRadius: 20, padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#d94f4f", boxShadow: "0 2px 10px rgba(0,0,0,0.15)", whiteSpace: "nowrap" }}>
            Back to U of M
          </button>
        </div>
      )}

      {/* ── Bottom-left: Report button + Legend ── */}
      <div style={{ position: "absolute", bottom: 24, left: 14, zIndex: 10, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
        {/* Report/Feedback button */}
        <button onClick={() => setFeedbackOpen(true)}
          style={{ display: "flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.97)", border: "1px solid #e2e8f0", borderRadius: 10, padding: "7px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#475569", boxShadow: "0 2px 8px rgba(0,0,0,0.10)", whiteSpace: "nowrap" }}>
          ⚑ Report / Feedback
        </button>
        {/* Legend */}
        <div style={{ background: "rgba(255,255,255,0.96)", border: "1px solid #e2e8f0", borderRadius: 10, padding: isMobile ? "7px 10px" : "10px 14px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", gap: isMobile ? 4 : 5 }}>
          <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: "#94a3b8", marginBottom: 1 }}>LEGEND</div>
          {LEGEND_ITEMS.map(({ color, border, label, short }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: isMobile ? 8 : 11, height: isMobile ? 8 : 11, borderRadius: "50%", background: color, border: `1.5px solid ${border}`, flexShrink: 0 }} />
              <span style={{ fontSize: isMobile ? 10 : 11, color: "#475569" }}>{isMobile ? short : label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom bar: Pickup/Dropoff ── */}
      <div style={{ position: "absolute", bottom: 24, left: 0, right: 0, zIndex: 10, display: "flex", justifyContent: "center", alignItems: "center", padding: "0 14px", pointerEvents: "none" }}>
        <button
          onClick={() => setParkingFilter((f) => f === "dropoff" ? null : "dropoff")}
          style={{ pointerEvents: "auto", display: "flex", alignItems: "center", gap: 7, background: filterActive("dropoff") ? "#00897b" : "rgba(255,255,255,0.97)", border: `2px solid ${filterActive("dropoff") ? "#00897b" : "#e2e8f0"}`, borderRadius: 24, padding: "10px 20px", cursor: "pointer", fontSize: 13, fontWeight: 700, color: filterActive("dropoff") ? "#fff" : "#00695c", boxShadow: "0 4px 16px rgba(0,0,0,0.13)" }}>
          Pickup/Dropoff Zones
          {filterActive("dropoff") && <span style={{ fontSize: 11, marginLeft: 2 }}>✓</span>}
        </button>
      </div>

      {/* ── Report/Feedback modal ── */}
      {feedbackOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 2000, padding: isMobile ? 0 : 20 }}
          onClick={() => setFeedbackOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: isMobile ? "20px 20px 0 0" : 16, width: "100%", maxWidth: 380, boxShadow: "0 16px 48px rgba(0,0,0,0.25)", overflow: "hidden" }}>
            {isMobile && <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "#e2e8f0" }} /></div>}
            <div style={{ padding: "20px 20px 6px", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#475569", lineHeight: 1.5, paddingRight: 12 }}>
                We want to make the user experience the best it can be, so please report any issues or suggestions with an Email or Instagram DMs!
              </div>
              <button onClick={() => setFeedbackOpen(false)} style={{ background: "none", border: "none", fontSize: 20, color: "#aaa", cursor: "pointer", padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
            </div>
            <div style={{ padding: "8px 20px 24px", display: "flex", flexDirection: "column", gap: 10 }}>
              <a href="mailto:umapscontact@gmail.com?subject=UMaps%20Parking%20Map%20Report" style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 12, border: "1.5px solid #e2e8f0", background: "#f8fafc", textDecoration: "none", color: "#1e293b" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fff3f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 18 }}>✉</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Email Us</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>umapscontact@gmail.com</div>
                </div>
              </a>
              <a href="https://www.instagram.com/myumaps/" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 12, border: "1.5px solid #e2e8f0", background: "#f8fafc", textDecoration: "none", color: "#1e293b" }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fdf0f8", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <radialGradient id="ig-modal" cx="30%" cy="107%" r="150%">
                        <stop offset="0%" stopColor="#fdf497"/><stop offset="5%" stopColor="#fdf497"/>
                        <stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/>
                        <stop offset="90%" stopColor="#285AEB"/>
                      </radialGradient>
                    </defs>
                    <rect x="2" y="2" width="20" height="20" rx="6" ry="6" fill="url(#ig-modal)"/>
                    <circle cx="12" cy="12" r="4.5" stroke="#fff" strokeWidth="1.8" fill="none"/>
                    <circle cx="17.5" cy="6.5" r="1.2" fill="#fff"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>DM us on Instagram</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>@myumaps</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Location error ── */}
      {locError && (
        <div style={{ position: "absolute", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "#fff3f0", border: "1px solid #ffcdd2", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#c62828", zIndex: 30, boxShadow: "0 2px 10px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          {locError}
          <button onClick={() => setLocError(null)} style={{ background: "none", border: "none", fontSize: 16, color: "#c62828", cursor: "pointer", padding: 0 }}>✕</button>
        </div>
      )}

      {/* ── Welcome / quick-select popup ── */}
      {welcomeOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 2000, padding: isMobile ? 0 : 20 }}
          onClick={() => setWelcomeOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            {/* Drag handle */}
            {isMobile && <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "#e2e8f0" }} /></div>}
            <div style={{ padding: isMobile ? "16px 20px 4px" : "24px 24px 4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                <img src="/UMaps Logo Circle Border.png" alt="UMaps" style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: "50%", flexShrink: 0 }} onError={e => e.target.style.display = "none"} />
                <div style={{ fontSize: isMobile ? 18 : 20, fontWeight: 800, color: "#1e293b" }}>UMaps Parking Map</div>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 6, lineHeight: 1.6 }}>
                Some information on this application may be outdated, please report any inaccurate information to{" "}
                <a href="mailto:umapscontact@gmail.com" style={{ color: "#1a73e8", fontWeight: 600, textDecoration: "none" }}>umapscontact@gmail.com</a>
                {" "}or our instagram{" "}
                <a href="https://www.instagram.com/myumaps/" target="_blank" rel="noopener noreferrer" style={{ color: "#d6249f", fontWeight: 600, textDecoration: "none" }}>@myumaps</a>.{" "}
                <strong style={{ color: "#1e293b" }}>ALWAYS CHECK POSTED SIGNS!</strong>{" "}
                In case of discrepancy between lot signage and the map, lot signage shall prevail.
              </div>
            </div>
            <div style={{ padding: isMobile ? "14px 20px" : "16px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.06em", color: "#475569", marginBottom: 10 }}>QUICK FILTERS — WHAT ARE YOU LOOKING FOR?</div>
              {/* Full-width "Parking Right Now" button */}
              <button onClick={() => handleWelcomeFilter("now")}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", borderRadius: 12, border: "2px solid #0ea5e9", background: "linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%)", cursor: "pointer", textAlign: "left", marginBottom: 8 }}>
                <span style={{ fontSize: isMobile ? 24 : 28 }}>🕐</span>
                <div>
                  <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 800, color: "#0c4a6e" }}>Parking Right Now</div>
                  <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2 }}>Show what's available at the current time</div>
                </div>
              </button>
              {/* 2×2 grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                {[
                  { filter: "paid_hours", icon: "💳", label: "School Hours (Paid Parking)",   sub: "7:30 AM – 4:30 PM",    bg: "#fff5f5", border: "#fca5a5", color: "#7f1d1d" },
                  { filter: "free_hours", icon: "🌆", label: "After 4:30 PM",  sub: "Free & evening lots",   bg: "#f0fdf4", border: "#86efac", color: "#166534" },
                  { filter: "accessible", icon: "♿", label: "Accessible",      sub: "Accessible spots only", bg: "#f5f3ff", border: "#c4b5fd", color: "#4c1d95" },
                  { filter: "dropoff",    icon: "🚗", label: "Pickup/Dropoff Zones", sub: "Quickest spots for pickup/dropoff", bg: "#f0fdfa", border: "#5eead4", color: "#134e4a" },
                ].map(({ filter, icon, label, sub, bg, border, color }) => (
                  <button key={filter} onClick={() => handleWelcomeFilter(filter)}
                    style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${border}`, background: bg, cursor: "pointer", textAlign: "left", gap: 3 }}>
                    <span style={{ fontSize: isMobile ? 20 : 22 }}>{icon}</span>
                    <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color }}>{label}</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>{sub}</div>
                  </button>
                ))}
              </div>
              <button onClick={() => setWelcomeOpen(false)}
                style={{ width: "100%", padding: "12px 0", borderRadius: 10, border: "1.5px solid #e2e8f0", background: "#f8fafc", color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                View All Parking Lots
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Parking info popup ── */}
      {parkingInfoData && (() => {
        const entry        = parkingInfoData;
        const paid         = isPaidNow(entry.paidSchedule);
        const isPermitOnly = entry.paidSchedule?.type === "permit_only";
        const isDrop       = (entry.category || "").toLowerCase().includes("drop");
        const isAccessible = (entry.category || "").toLowerCase().includes("accessible");
        const nLot         = isNLot(entry.name);
        const schedText    = formatSchedule(entry.paidSchedule, entry.name);
        const polyN        = entry.polygon?.length || 0;
        const centroidLat  = polyN > 0 ? entry.polygon.reduce((s, c) => s + c.latitude,  0) / polyN : null;
        const centroidLng  = polyN > 0 ? entry.polygon.reduce((s, c) => s + c.longitude, 0) / polyN : null;

        return (
          <div onClick={() => setParkingInfoData(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1500, padding: isMobile ? 0 : "20px 14px" }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: isMobile ? "20px 20px 0 0" : 16, overflow: "hidden", width: "100%", maxWidth: 440, maxHeight: isMobile ? "85dvh" : "90dvh", overflowY: "auto", boxShadow: "0 16px 48px rgba(0,0,0,0.30)" }}>

              {/* Drag handle on mobile */}
              {isMobile && <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "#e2e8f0" }} /></div>}

              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 18px 12px", background: isDrop ? "#e0f2f1" : isAccessible ? "#f3e8ff" : "#fff8e6", borderBottom: `2px solid ${isDrop ? "#80cbc4" : isAccessible ? "#d8b4fe" : "#f0d060"}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 }}>{entry.name}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>{entry.category}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, paddingLeft: 10 }}>
                  <button onClick={() => { const url = `${window.location.origin}/parking?p=${slugify(entry.name)}${parkingFilter ? `&f=${parkingFilter}` : ""}`; navigator.clipboard.writeText(url).then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); }); }}
                    style={{ fontSize: 11, fontWeight: 600, color: linkCopied ? "#2e7d32" : "#d94f4f", background: linkCopied ? "#e6f4ea" : "#fff5f5", border: `1px solid ${linkCopied ? "#b7dfbd" : "#d94f4f"}`, borderRadius: 6, padding: "5px 9px", cursor: "pointer", whiteSpace: "nowrap" }}>
                    {linkCopied ? "✓ Copied!" : "🔗 Share"}
                  </button>
                  {centroidLat && (
                    <button onClick={() => {
                      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                      const url = isIOS
                        ? `maps://maps.apple.com/?q=${centroidLat.toFixed(6)},${centroidLng.toFixed(6)}`
                        : `https://maps.google.com/maps?q=${centroidLat.toFixed(6)},${centroidLng.toFixed(6)}`;
                      window.open(url, "_blank");
                    }} style={{ fontSize: 11, fontWeight: 600, color: "#1a73e8", background: "#e8f0fe", border: "1px solid #a8c7fa", borderRadius: 6, padding: "5px 9px", cursor: "pointer", whiteSpace: "nowrap" }}>
                      Open on Maps
</button>
                  )}
                  <button onClick={() => setParkingInfoData(null)} style={{ background: "none", border: "none", fontSize: 20, color: "#aaa", cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                </div>
              </div>

              <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>

                {isDrop ? (
                  <div style={{ background: "#e0f2f1", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#00695c", fontWeight: 600 }}>
                    Short-term drop off / pick up only. Do not leave your vehicle unattended.
                  </div>
                ) : isAccessible ? (
                  <>
                    <div style={{ background: "#f3e8ff", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#4c1d95", fontWeight: 600 }}>
                      Must display a valid Manitoba Accessible Parking Permit.
                    </div>
                    {entry.hourlyRate && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 3 }}>RATE</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>{entry.hourlyRate}/hr</div>
                        </div>
                        {!isPermitOnly && (
                          <div style={{ padding: "5px 13px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: paid ? "#fdecea" : "#e6f4ea", color: paid ? "#c62828" : "#2e7d32", border: `1px solid ${paid ? "#f5c6cb" : "#b7dfbd"}` }}>
                            {paid ? "PAID NOW" : "FREE NOW"}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>{schedText}</div>
                  </>
                ) : (
                  <>
                    {/* Schedule + badge */}
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 4 }}>
                          {nLot ? "SCHEDULE" : "PAID HOURS (FREE OUTSIDE THESE)"}
                        </div>
                        <div style={{ fontSize: 12, color: "#333", lineHeight: 1.6, whiteSpace: "pre-line" }}>{schedText}</div>
                      </div>
                      {!isPermitOnly && !nLot && (
                        <div style={{ flexShrink: 0, padding: "5px 11px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: paid ? "#fdecea" : "#e6f4ea", color: paid ? "#c62828" : "#2e7d32", border: `1px solid ${paid ? "#f5c6cb" : "#b7dfbd"}` }}>
                          {paid ? "PAID NOW" : "FREE NOW"}
                        </div>
                      )}
                    </div>

                    <div style={{ borderTop: "1px solid #f0f0f0" }} />

                    {/* Rates row */}
                    <div style={{ display: "flex" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 4 }}>HOURLY</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{entry.hourlyRate || "—"}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 4 }}>DAILY MAX</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{entry.dailyMax || "—"}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 4 }}>TIME LIMIT</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{entry.timeLimit || "—"}</div>
                      </div>
                    </div>

                    <div style={{ borderTop: "1px solid #f0f0f0" }} />

                    {/* HONK */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#f9f6ee", borderRadius: 10, border: "1px solid #e8d88a" }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#bbb", marginBottom: 3 }}>HONK ZONE #</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#222" }}>{entry.zone || "N/A"}</div>
                      </div>
                      {entry.payLink ? (
                        <a href={entry.payLink} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 13, fontWeight: 700, color: "#fff", background: "#d94f4f", borderRadius: 8, padding: "9px 16px", textDecoration: "none", boxShadow: "0 2px 6px rgba(217,79,79,0.30)" }}>
                          Pay with HONK →
                        </a>
                      ) : (
                        <div style={{ fontSize: 12, color: "#aaa", fontStyle: "italic" }}>No HONK payment</div>
                      )}
                    </div>
                  </>
                )}

                {/* Description */}
                {entry.description && (
                  <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7, padding: "10px 12px", background: "#f7f7f7", borderRadius: 8 }}>{entry.description}</div>
                )}

                {/* Disclaimer */}
                <div style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.6, borderTop: "1px solid #f0f0f0", paddingTop: 10 }}>
                  Parking information is provided for reference only. Hours and rates may change — always check posted signs. In case of discrepancy between lot signage and the map, lot signage shall prevail.
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Volunteer popup ── */}
      {volunteerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 20 }} onClick={() => setVolunteerOpen(false)}>
          <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 24, maxWidth: isMobile ? "100%" : 640, width: "100%", boxShadow: "0 32px 96px rgba(0,0,0,0.4)", maxHeight: isMobile ? "92dvh" : "90vh", overflowY: "auto", display: "flex", flexDirection: "column", ...(isMobile ? { position: "absolute", bottom: 0, left: 0, right: 0 } : {}) }} onClick={(e) => e.stopPropagation()}>
            {isMobile && <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, paddingBottom: 4 }}><div style={{ width: 36, height: 4, borderRadius: 2, background: "#e2e8f0" }} /></div>}
            <div style={{ padding: isMobile ? "16px 24px 0" : "32px 32px 0", textAlign: "center" }}>
              <img src="/UMaps Logo Circle Border.png" alt="UMaps" style={{ width: isMobile ? 48 : 60, height: isMobile ? 48 : 60, borderRadius: "50%", marginBottom: 12 }} onError={e => e.target.style.display = "none"} />
              <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, color: "#0f172a" }}>Volunteer for UMaps</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 5, lineHeight: 1.5 }}>Help us build a better campus experience for U of M students.</div>
            </div>
            {(() => {
              const slides = [
                { src: "/Map Info/images/IndoorGPS.png",    caption: "Indoor Campus GPS" },
                { src: "/Map Info/images/Parking Info.png", caption: "Better Parking Maps" },
                { src: "/Map Info/images/Guides.png",       caption: "Visitor Guides" },
              ];
              return (
                <div style={{ padding: isMobile ? "20px 20px 0" : "24px 28px 0" }}>
                  <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", border: "1.5px solid #e2e8f0", background: "#f0f4f8", maxWidth: "67%", margin: "0 auto" }}>
                    <img src={slides[slideIndex].src} alt={slides[slideIndex].caption} style={{ width: "100%", height: "auto", display: "block" }} />
                    <button onClick={() => setSlideIndex((i) => (i + slides.length - 1) % slides.length)} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", background: "rgba(255,255,255,0.88)", fontSize: 18, fontWeight: 700 }}>‹</button>
                    <button onClick={() => setSlideIndex((i) => (i + 1) % slides.length)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", width: 36, height: 36, borderRadius: "50%", border: "none", cursor: "pointer", background: "rgba(255,255,255,0.88)", fontSize: 18, fontWeight: 700 }}>›</button>
                  </div>
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>{slides[slideIndex].caption}</div>
                    <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                      {slides.map((_, i) => <button key={i} onClick={() => setSlideIndex(i)} style={{ width: i === slideIndex ? 20 : 7, height: 7, borderRadius: 4, border: "none", cursor: "pointer", padding: 0, background: i === slideIndex ? "#0f172a" : "#cbd5e1", transition: "all 0.2s" }} />)}
                    </div>
                  </div>
                </div>
              );
            })()}
            <div style={{ margin: isMobile ? "18px 20px 0" : "20px 28px 0", borderTop: "1px solid #f1f5f9" }} />
            <div style={{ padding: isMobile ? "16px 24px 4px" : "18px 32px 4px", fontSize: 13, color: "#475569", lineHeight: 1.8, textAlign: "center" }}>
              We have more features planned for the near future. If you'd like to volunteer and help the UMaps Initiative, please reach out at{" "}
              <a href="mailto:umapscontact@gmail.com" style={{ color: "#1a73e8", fontWeight: 600, textDecoration: "none" }}>umapscontact@gmail.com</a>!
            </div>
            <div style={{ padding: isMobile ? "16px 24px 32px" : "20px 32px 28px", display: "flex", justifyContent: "center" }}>
              <button onClick={() => setVolunteerOpen(false)} style={{ background: "#0f172a", color: "#fff", border: "none", borderRadius: 12, padding: "13px 48px", fontSize: 14, fontWeight: 700, cursor: "pointer", width: isMobile ? "100%" : "auto" }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
